<template>
  <div class="parser-page">
    <div class="parser-container">
      <div class="header-section">
        <h1>📹 {{ $t('parser.title') }}</h1>
        <p>{{ $t('parser.subtitle') }}</p>
      </div>

      <!-- Input Section with History -->
      <div class="input-section">
        <HistoryInput
          v-model="videoUrl"
          placeholder="https://www.youtube.com/watch?v=..."
          size="large"
          class="url-input"
          storage-key="parser_video_url"
          :max-history="20"
          @enter="parseVideo"
          ref="urlInputRef"
        >
          <template #prefix>
            <el-icon><Link /></el-icon>
          </template>
        </HistoryInput>
        <el-button 
          type="primary" 
          size="large" 
          :loading="loading" 
          @click="parseVideo"
          class="parse-btn"
        >
          <el-icon><VideoPlay /></el-icon>
          {{ $t('parser.parseBtn') }}
        </el-button>
      </div>

      <!-- Quick Sample Fill Buttons -->
      <div class="sample-links-bar" style="margin-top: 12px; margin-bottom: 20px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
        <span style="font-size: 13px; color: #909399; font-weight: 500;">💡 快速试用示例:</span>
        <el-button size="small" round plain type="primary" @click="fillSampleUrl('bilibili')">B站 示例</el-button>
        <el-button size="small" round plain type="success" @click="fillSampleUrl('douyin')">抖音 示例</el-button>
        <el-button size="small" round plain type="danger" @click="fillSampleUrl('youtube')">YouTube 示例</el-button>
        <el-button size="small" round plain type="warning" @click="fillSampleUrl('xhs')">小红书 示例</el-button>
      </div>

      <!-- Quick Links (最近使用) -->
      <div v-if="recentLinks.length > 0" class="recent-links">
        <div class="recent-header">
          <span class="recent-title">📋 {{ $t('parser.recentParse') }}</span>
          <el-button type="primary" link size="small" @click="toggleShowAll">
            {{ showAllRecent ? '收起' : `查看全部 (${urlHistory.length})` }}
          </el-button>
        </div>
        <div class="recent-tags">
          <el-tag 
            v-for="(item, index) in recentLinks" 
            :key="index"
            :type="getPlatformTagType(item.platform)"
            closable
            @click="useRecentLink(item)"
            @close="deleteHistory(item)"
            class="recent-tag"
          >
            <el-icon class="tag-icon"><VideoCamera /></el-icon>
            {{ item.title || truncateUrl(item.url, 30) }}
          </el-tag>
        </div>
      </div>

      <!-- Error Message -->
      <el-alert
        v-if="error"
        :title="error"
        type="error"
        show-icon
        :closable="false"
        class="error-alert"
      />

      <!-- Result Section -->
      <transition name="el-fade-in">
        <div v-if="result" class="result-section">
          <!-- Video Info Card -->
          <el-card class="video-info-card" shadow="hover">
            <div class="video-header">
              <el-image 
                :src="result.thumbnail_url" 
                fit="cover"
                class="video-thumbnail"
              >
                <template #error>
                  <div class="image-placeholder">
                    <el-icon><Picture /></el-icon>
                  </div>
                </template>
              </el-image>
              <div class="video-info">
                <h2>{{ result.title }}</h2>
                <div class="video-meta">
                  <el-tag :type="getPlatformTagType(result.platform)" size="small">
                    {{ result.platform.toUpperCase() }}
                  </el-tag>
                  <span class="author">{{ result.author }}</span>
                  <span class="duration">⏱ {{ formatDuration(result.duration) }}</span>
                </div>
              </div>
            </div>
          </el-card>

          <!-- Quality Options with Tabs -->
          <el-card class="quality-table-card" shadow="hover">
            <el-tabs v-model="activeDownloadTab" type="card" class="download-tabs">
              <!-- Video Tab -->
              <el-tab-pane name="video">
                <template #label>
                  <span class="tab-label">
                    <el-icon><VideoPlay /></el-icon> Video
                  </span>
                </template>
                
                <el-table :data="qualityOptions" stripe style="width: 100%">
                  <el-table-column label="Quality" width="200">
                    <template #default="scope">
                      <div class="quality-cell">
                        <span class="quality-text">{{ scope.row.quality }}</span>
                        <el-tag v-if="scope.row.hasAudio" size="small" type="success">
                          <el-icon><Microphone /></el-icon>
                        </el-tag>
                        <el-tag v-if="scope.row.badge" size="small" :type="scope.row.badgeType">
                          {{ scope.row.badge }}
                        </el-tag>
                      </div>
                    </template>
                  </el-table-column>
                  
                  <el-table-column label="File size" align="center">
                    <template #default="scope">
                      <span class="file-size">{{ scope.row.size }}</span>
                    </template>
                  </el-table-column>
                  
                  <el-table-column label="" width="150" align="center">
                    <template #default="scope">
                      <el-button 
                        type="success" 
                        :loading="scope.row.downloading"
                        @click="handleDownload(scope.row)"
                      >
                        <el-icon><Download /></el-icon> Download
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>
              
              <!-- Sound Only Tab -->
              <el-tab-pane name="audio">
                <template #label>
                  <span class="tab-label">
                    <el-icon><Headset /></el-icon> Sound Only
                  </span>
                </template>
                
                <el-table :data="audioOptions" stripe style="width: 100%">
                  <el-table-column label="Quality" width="200">
                    <template #default="scope">
                      <div class="quality-cell">
                        <span class="quality-text">{{ scope.row.quality }}</span>
                        <el-tag size="small" :type="scope.row.badgeType">
                          {{ scope.row.badge }}
                        </el-tag>
                      </div>
                    </template>
                  </el-table-column>
                  
                  <el-table-column label="Format" align="center">
                    <template #default="scope">
                      <span class="file-size">{{ scope.row.format }}</span>
                    </template>
                  </el-table-column>
                  
                  <el-table-column label="" width="150" align="center">
                    <template #default="scope">
                      <el-button 
                        type="success" 
                        :loading="scope.row.downloading"
                        @click="handleAudioDownload(scope.row)"
                      >
                        <el-icon><Download /></el-icon> Download
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>
              
              <!-- Video without Sound Tab -->
              <el-tab-pane name="mute">
                <template #label>
                  <span class="tab-label">
                    <el-icon><Mute /></el-icon> Video without Sound
                  </span>
                </template>
                
                <el-table :data="muteVideoOptions" stripe style="width: 100%">
                  <el-table-column label="Quality" width="200">
                    <template #default="scope">
                      <div class="quality-cell">
                        <span class="quality-text">{{ scope.row.quality }}</span>
                        <el-tag size="small" type="info">无声</el-tag>
                        <el-tag v-if="scope.row.badge" size="small" :type="scope.row.badgeType">
                          {{ scope.row.badge }}
                        </el-tag>
                      </div>
                    </template>
                  </el-table-column>
                  
                  <el-table-column label="File size" align="center">
                    <template #default="scope">
                      <span class="file-size">{{ scope.row.size }}</span>
                    </template>
                  </el-table-column>
                  
                  <el-table-column label="" width="150" align="center">
                    <template #default="scope">
                      <el-button 
                        type="success" 
                        :loading="scope.row.downloading"
                        @click="handleDownload(scope.row)"
                      >
                        <el-icon><Download /></el-icon> Download
                      </el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </el-tab-pane>
            </el-tabs>

            <div class="quality-note">
              <el-icon><StarFilled /></el-icon>
              <span>1080P: Universal ⭐ 2K: Premium mobile ⭐ 4K: High-end PC/TV</span>
              <el-icon><StarFilled /></el-icon>
            </div>
            
            <!-- 提取到的视频链接 -->
            <div v-if="extractedVideoUrl" class="extracted-url-section">
              <div class="url-header">
                <el-icon><VideoPlay /></el-icon>
                <span>视频直链已提取成功！点击下方链接打开：</span>
              </div>
              <a 
                :href="extractedVideoUrl" 
                target="_blank" 
                rel="noopener noreferrer"
                class="video-link"
              >
                {{ extractedVideoUrl.length > 80 ? extractedVideoUrl.substring(0, 80) + '...' : extractedVideoUrl }}
              </a>
              <div class="url-actions">
                <el-button type="primary" size="small" @click="openExtractedUrl">
                  <el-icon><Link /></el-icon> 打开视频
                </el-button>
                <el-button size="small" @click="copyExtractedUrl">
                  <el-icon><CopyDocument /></el-icon> 复制链接
                </el-button>
              </div>
            </div>
          </el-card>
        </div>
      </transition>

      <!-- Sniffed Resources -->
      <transition name="el-zoom-in-top">
        <div class="ingested-section">
          <el-card class="ingested-card" shadow="hover">
            <template #header>
              <div class="card-header">
                <div class="header-left">
                  <el-icon class="pulse-icon"><Connection /></el-icon>
                  <span class="header-title">桌面嗅探助手抓取到的视频</span>
                  <el-tag size="small" type="success" effect="dark" round class="ml-2">{{ ingestedResources.length }}</el-tag>
                </div>
                <el-button v-if="ingestedResources.length > 0" type="danger" text @click="clearIngestedResources">清空列表</el-button>
              </div>
            </template>
            
            <div v-if="ingestedResources.length > 0" class="ingested-list">
              <div v-for="(item, index) in ingestedResources" :key="index" class="ingested-item">
                <el-image 
                  :src="item.thumbnail || 'https://cube.elemecdn.com/e/fd/0fc7d20532fdaf769a25683617711png.png'" 
                  class="ingested-thumb" 
                  fit="cover"
                />
                <div class="ingested-info">
                  <div class="ingested-title">{{ item.title || '未知视频' }}</div>
                  <div class="ingested-meta">
                    <el-tag size="small" type="info">{{ item.platform || '未知平台' }}</el-tag>
                    <span class="ingested-time">{{ new Date(item.ingested_at).toLocaleTimeString() }}</span>
                  </div>
                </div>
                <div class="ingested-actions">
                  <el-button type="success" size="small" @click="downloadIngested(item)">
                    <el-icon><Download /></el-icon> 下载视频
                  </el-button>
                </div>
              </div>
            </div>
            
            <div v-else class="ingested-empty">
              <el-empty description="暂无嗅探记录，请开启桌面助手后在微信中刷视频" :image-size="80"></el-empty>
            </div>
          </el-card>
        </div>
      </transition>

      <!-- Supported Platforms -->
      <div class="supported-platforms">
        <h3>✅ {{ $t('parser.supportedPlatforms') }}</h3>
        <div class="platform-tags">
          <el-tag effect="plain" round>Bilibili</el-tag>
          <el-tag effect="plain" round type="success">抖音</el-tag>
          <el-tag effect="plain" round type="danger">小红书</el-tag>
          <el-tag effect="plain" round type="warning">YouTube</el-tag>
          <el-tag effect="plain" round type="info">TikTok</el-tag>
          <el-tag effect="plain" round>快手</el-tag>
          <el-tag effect="plain" round type="success">微博</el-tag>
          <el-tag effect="plain" round type="danger">西瓜视频</el-tag>
          <el-tag effect="plain" round type="warning">Instagram</el-tag>
          <el-tag effect="plain" round type="info">Twitter/X</el-tag>
          <el-tag effect="plain" round>Facebook</el-tag>
          <el-tag effect="plain" round type="success">知乎</el-tag>
          <el-tag effect="plain" round type="danger">AcFun</el-tag>
          <el-tag effect="plain" round type="warning">好看视频</el-tag>
          <el-tag effect="plain" round type="info">梨视频</el-tag>
          <el-tag effect="plain" round>更多...</el-tag>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <!-- Loading Overlay -->
    <el-dialog
      v-model="extracting"
      :title="dialogMode === 'extract' ? '正在智能解析视频链接' : dialogMode === 'audio_download' ? '正在高速下载并转码音频' : '正在云端下载与转码合并视频'"
      width="420px"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :show-close="false"
    >
      <div class="extracting-content">
        <el-icon class="is-loading" :size="40"><Loading /></el-icon>
        <p style="font-weight: bold; font-size: 16px; margin-top: 15px;">
          {{ dialogMode === 'extract' ? '正在智能解析并提取多平台音视频链接...' : dialogMode === 'audio_download' ? '正在云端极速下载高清音频并转码...' : '正在全速多线程下载高清视频与音频合并...' }}
        </p>
        <p class="tip" style="color: #666; font-size: 13px; margin: 10px 0;">
          {{ dialogMode === 'extract' ? '这通常需要 5-15 秒，请耐心等待' : '高清音视频文件较大，多线程分片下载与合并转码可能需要 30 秒至 3 分钟，已启动高速通道，请耐心等待...' }}
        </p>
        <el-progress :percentage="extractProgress" :stroke-width="8" status="success" :striped="dialogMode !== 'extract'" :striped-flow="dialogMode !== 'extract'" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Link, Picture, Download, Microphone, StarFilled, Loading, Delete, VideoPlay, VideoCamera, Headset, Mute, CopyDocument, Connection } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import HistoryInput from '@/components/HistoryInput.vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/authService'

const { t } = useI18n()

interface UrlHistoryItem {
  url: string
  title: string
  platform: string
  thumbnail: string
  time: string
  timestamp: number
}

interface VideoQuality {
  quality: string
  width: number
  height: number
  download_url: string
}

interface ParseResult {
  success: boolean
  platform: string
  video_id: string
  title: string
  author: string
  duration: number
  thumbnail_url: string
  qualities: VideoQuality[]
}

interface QualityOption {
  quality: string
  size: string
  hasAudio: boolean
  badge?: string
  badgeType?: string
  downloading: boolean
  originalUrl: string
}

interface AudioOption {
  quality: string
  format: string
  badge: string
  badgeType: string
  downloading: boolean
  formatId: string
}

const router = useRouter()
const videoUrl = ref('')
const loading = ref(false)
const error = ref('')
const result = ref<ParseResult | null>(null)
const extracting = ref(false)
const dialogMode = ref<'extract' | 'download' | 'audio_download'>('extract')
const extractProgress = ref(0)
const qualityOptions = ref<QualityOption[]>([])
const audioOptions = ref<AudioOption[]>([])
const muteVideoOptions = ref<QualityOption[]>([])
const activeDownloadTab = ref('video')
const urlHistory = ref<UrlHistoryItem[]>([])
const showAllRecent = ref(false)
const extractedVideoUrl = ref('')
const urlInputRef = ref<InstanceType<typeof HistoryInput>>()

// 嗅探助手状态
const ingestedResources = ref<any[]>([])
let ingestedTimer: number | null = null

// 最大历史记录数
const MAX_HISTORY = 50

// 填充示例链接
const fillSampleUrl = (platform: string) => {
  const samples: Record<string, string> = {
    bilibili: 'https://www.bilibili.com/video/BV1xx411c7mD',
    douyin: 'https://v.douyin.com/iJ8xY123/',
    youtube: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
    xhs: 'https://www.xiaohongshu.com/explore/648a1234000000001f012345'
  }
  if (samples[platform]) {
    videoUrl.value = samples[platform]
    ElMessage.info(`已填入 ${platform.toUpperCase()} 示例链接`)
  }
}

// 打开提取到的视频链接
const openExtractedUrl = () => {
  if (extractedVideoUrl.value) {
    window.open(extractedVideoUrl.value, '_blank')
  }
}

// 复制提取到的视频链接
const copyExtractedUrl = async () => {
  if (extractedVideoUrl.value) {
    try {
      await navigator.clipboard.writeText(extractedVideoUrl.value)
      ElMessage.success('链接已复制到剪贴板')
    } catch (e) {
      ElMessage.error('复制失败')
    }
  }
}

// 计算最近链接 (显示前5个或全部)
const recentLinks = computed(() => {
  if (showAllRecent.value) {
    return urlHistory.value
  }
  return urlHistory.value.slice(0, 5)
})

// 切换显示全部
const toggleShowAll = () => {
  showAllRecent.value = !showAllRecent.value
}

// 嗅探助手轮询
const fetchIngestedResources = async () => {
  try {
    const res = await api.get('/api/parse/ingested')
    if (res.data.success && res.data.data) {
      ingestedResources.value = res.data.data
    }
  } catch (err) {
    console.error('Failed to fetch ingested resources:', err)
  }
}

// 清空嗅探历史
const clearIngestedResources = async () => {
  try {
    const res = await api.delete('/api/parse/ingested')
    if (res.data.success) {
      ingestedResources.value = []
      ElMessage.success('已清空嗅探列表')
    }
  } catch (err) {
    ElMessage.error('清空失败')
  }
}

// 下载嗅探资源
const downloadIngested = (item: any) => {
  window.open(item.url, '_blank')
}

// 生命周期挂载
onMounted(() => {
  loadHistory()
  fetchIngestedResources()
  ingestedTimer = window.setInterval(fetchIngestedResources, 3000)
})

onUnmounted(() => {
  if (ingestedTimer) {
    clearInterval(ingestedTimer)
  }
})

// 加载历史记录
const loadHistory = () => {
  try {
    const saved = localStorage.getItem('videoUrlHistory')
    if (saved) {
      urlHistory.value = JSON.parse(saved)
    }
  } catch (e) {
    console.error('Failed to load history:', e)
    urlHistory.value = []
  }
}

// 保存历史记录
const saveHistory = () => {
  try {
    localStorage.setItem('videoUrlHistory', JSON.stringify(urlHistory.value))
  } catch (e) {
    console.error('Failed to save history:', e)
  }
}

// 添加到历史记录
const addToHistory = (url: string, title: string, platform: string, thumbnail: string) => {
  // 检查是否已存在
  const existingIndex = urlHistory.value.findIndex(item => item.url === url)
  if (existingIndex !== -1) {
    // 移到最前面
    const existing = urlHistory.value.splice(existingIndex, 1)[0]
    existing.time = new Date().toLocaleString()
    existing.timestamp = Date.now()
    existing.title = title || existing.title
    existing.platform = platform || existing.platform
    existing.thumbnail = thumbnail || existing.thumbnail
    urlHistory.value.unshift(existing)
  } else {
    // 添加新记录
    urlHistory.value.unshift({
      url,
      title: title || '未知标题',
      platform: platform || 'unknown',
      thumbnail: thumbnail || '',
      time: new Date().toLocaleString(),
      timestamp: Date.now()
    })
  }
  
  // 限制数量
  if (urlHistory.value.length > MAX_HISTORY) {
    urlHistory.value = urlHistory.value.slice(0, MAX_HISTORY)
  }
  
  saveHistory()
}

// 删除历史记录
const deleteHistory = (item: UrlHistoryItem) => {
  const index = urlHistory.value.findIndex(h => h.url === item.url)
  if (index !== -1) {
    urlHistory.value.splice(index, 1)
    saveHistory()
    ElMessage.success('已删除')
  }
}

// 清空所有历史
const clearAllHistory = async () => {
  try {
    await ElMessageBox.confirm(
      '确定要清空所有历史记录吗？',
      '确认清空',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    )
    urlHistory.value = []
    saveHistory()
    ElMessage.success('历史记录已清空')
  } catch {
    // 用户取消
  }
}

// 使用最近链接
const useRecentLink = (item: UrlHistoryItem) => {
  videoUrl.value = item.url
  parseVideo()
}

// 自动补全查询
const querySearch = (queryString: string, cb: (results: UrlHistoryItem[]) => void) => {
  const results = queryString
    ? urlHistory.value.filter(item => 
        item.url.toLowerCase().includes(queryString.toLowerCase()) ||
        (item.title && item.title.toLowerCase().includes(queryString.toLowerCase())) ||
        (item.platform && item.platform.toLowerCase().includes(queryString.toLowerCase()))
      )
    : urlHistory.value
  cb(results)
}

// 选择自动补全项
const handleSelect = (item: UrlHistoryItem) => {
  videoUrl.value = item.url
}

// 清空输入
const handleClearInput = () => {
  videoUrl.value = ''
}

// 截断URL显示
const truncateUrl = (url: string, maxLen: number = 50): string => {
  if (url.length <= maxLen) return url
  return url.substring(0, maxLen) + '...'
}

// 获取平台标签类型
const getPlatformTagType = (platform: string): string => {
  const types: Record<string, string> = {
    'youtube': 'danger',
    'douyin': 'success',
    'tiktok': 'info',
    'bilibili': '',
    'xiaohongshu': 'danger',
    'kuaishou': 'warning',
    'weibo': 'success',
    'instagram': 'warning',
    'twitter': 'info',
    'xigua': 'danger',
  }
  return types[platform.toLowerCase()] || ''
}

onMounted(() => {
  loadHistory()
  // 兼容旧版本
  const lastUrl = localStorage.getItem('lastVideoUrl')
  if (lastUrl && !urlHistory.value.find(h => h.url === lastUrl)) {
    videoUrl.value = lastUrl
  }
})

// 进度条动画
let progressTimer: ReturnType<typeof setInterval> | null = null

const startProgress = () => {
  extractProgress.value = 0
  progressTimer = setInterval(() => {
    if (dialogMode.value === 'extract') {
      if (extractProgress.value < 90) {
        extractProgress.value += Math.random() * 15
      }
    } else {
      // 视频下载时间较长，平缓自增动画提示下载状态
      if (extractProgress.value < 95) {
        extractProgress.value += Math.random() * 3
      }
    }
  }, 500)
}

const stopProgress = () => {
  if (progressTimer) {
    clearInterval(progressTimer)
    progressTimer = null
  }
  extractProgress.value = 100
}

const parseVideo = async () => {
  if (!videoUrl.value.trim()) {
    ElMessage.warning('请输入视频链接')
    return
  }

  // 提取链接中的 URL，去掉附带的文本
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const matches = videoUrl.value.match(urlRegex);
  let cleanUrl = videoUrl.value.trim();
  if (matches && matches.length > 0) {
    cleanUrl = matches[0];
    videoUrl.value = cleanUrl; // 更新输入框为干净的URL
  }

  // 保存到HistoryInput组件的历史记录
  if (urlInputRef.value) {
    urlInputRef.value.addToHistory(cleanUrl)
  }

  // Save to localStorage (兼容旧版)
  localStorage.setItem('lastVideoUrl', cleanUrl)

  loading.value = true
  error.value = ''
  result.value = null
  qualityOptions.value = []
  dialogMode.value = 'extract'

  try {
    console.log('Parsing video using unified extractor:', videoUrl.value)
    
    // 使用新的统一提取接口
    const response = await api.post('/api/parse/extract-unified', { url: videoUrl.value.trim() })
    const data = response.data
    console.log('Unified extract result:', data)
    
    if (!data.success) {
      throw new Error(data.message || '解析失败')
    }
    
    // 添加到历史记录
    addToHistory(
      videoUrl.value.trim(),
      data.title || '',
      data.platform || 'unknown',
      data.thumbnail || ''
    )
    
    // 转换为 ParseResult 格式
    const parseResult: ParseResult = {
      success: true,
      platform: data.platform || 'unknown',
      video_id: 'unknown',
      title: data.title || '未知标题',
      author: data.author || '未知作者',
      duration: data.duration || 0,
      thumbnail_url: data.thumbnail || '',
      qualities: []
    }
    
    result.value = parseResult
    
    // 生成质量选项
    generateQualityOptions(parseResult)
    
    ElMessage.success('解析成功')
  } catch (err: any) {
    console.error('Parse error:', err)
    if (err.response?.status === 401 || err.response?.status === 402) return // 拦截器已处理
    error.value = err.response?.data?.detail || err.message || '解析失败，请检查链接是否正确'
    ElMessage.error(error.value)
  } finally {
    loading.value = false
  }
}

const generateQualityOptions = (data: ParseResult) => {
  const platform = data.platform.toLowerCase()
  // 保存当前解析的视频URL
  const currentUrl = videoUrl.value.trim()
  
  if (platform === 'youtube') {
    qualityOptions.value = [
      { quality: 'Best (视频+音频)', size: t('parser.estimated'), hasAudio: true, badge: 'MP4', badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: '1080p', size: t('parser.estimated'), hasAudio: true, badge: 'FHD', badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: '720p', size: t('parser.estimated'), hasAudio: true, badge: 'HD', badgeType: 'primary', downloading: false, originalUrl: currentUrl },
    ]
  } else if (platform === 'bilibili') {
    qualityOptions.value = [
      { quality: '1080P 高清', size: t('parser.estimated'), hasAudio: true, badge: 'FHD', badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: '720P 高清', size: t('parser.estimated'), hasAudio: true, badge: 'HD', badgeType: 'primary', downloading: false, originalUrl: currentUrl },
      { quality: '480P 清晰', size: t('parser.estimated'), hasAudio: true, badge: '', badgeType: '', downloading: false, originalUrl: currentUrl },
    ]
  } else if (platform === 'douyin') {
    qualityOptions.value = [
      { quality: t('parser.qualityBestNoWatermark'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.noWatermark'), badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: t('parser.qualityOriginal'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.badgeDouyin'), badgeType: 'danger', downloading: false, originalUrl: currentUrl },
    ]
  } else if (platform === 'xiaohongshu' || platform === 'xhs') {
    qualityOptions.value = [
      { quality: t('parser.qualityBestNoWatermark'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.noWatermark'), badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: t('parser.qualityOriginal'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.badgeXHS'), badgeType: 'danger', downloading: false, originalUrl: currentUrl },
    ]
  } else if (platform === 'kuaishou' || platform === 'ks') {
    qualityOptions.value = [
      { quality: t('parser.qualityBestNoWatermark'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.noWatermark'), badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: t('parser.qualityOriginal'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.badgeKuaishou'), badgeType: 'warning', downloading: false, originalUrl: currentUrl },
    ]
  } else if (platform === 'tiktok') {
    qualityOptions.value = [
      { quality: t('parser.qualityBestNoWatermark'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.noWatermark'), badgeType: 'success', downloading: false, originalUrl: currentUrl },
      { quality: 'HD', size: t('parser.estimated'), hasAudio: true, badge: 'TikTok', badgeType: 'info', downloading: false, originalUrl: currentUrl },
    ]
  } else {
    qualityOptions.value = [
      { quality: t('parser.qualityBest'), size: t('parser.estimated'), hasAudio: true, badge: t('parser.noWatermark'), badgeType: 'success', downloading: false, originalUrl: currentUrl },
    ]
  }
  
  // 音频选项 - 支持多种格式
  audioOptions.value = [
    // 无损格式
    { quality: 'FLAC 无损', format: 'FLAC', badge: t('parser.lossless'), badgeType: 'danger', downloading: false, formatId: 'flac' },
    { quality: 'WAV 无损', format: 'WAV', badge: t('parser.lossless'), badgeType: 'danger', downloading: false, formatId: 'wav' },
    // 高质量有损
    { quality: 'MP3 320kbps', format: 'MP3', badge: 'HQ', badgeType: 'success', downloading: false, formatId: 'mp3_320' },
    // 标准有损
    { quality: 'MP3 256kbps', format: 'MP3', badge: t('parser.standard'), badgeType: 'primary', downloading: false, formatId: 'mp3_256' },
    { quality: 'MP3 192kbps', format: 'MP3', badge: t('parser.normal'), badgeType: 'info', downloading: false, formatId: 'mp3_192' },
  ]
  
  // 无声视频选项
  muteVideoOptions.value = [
    { quality: t('parser.qualityBestMute'), size: t('parser.estimated'), hasAudio: false, badge: t('parser.mute'), badgeType: 'info', downloading: false, originalUrl: currentUrl },
  ]
}

// 处理音频下载
const handleAudioDownload = async (option: AudioOption) => {
  if (!result.value) return
  
  option.downloading = true
  dialogMode.value = 'audio_download'
  extracting.value = true
  startProgress()
  
  try {
    const originalPageUrl = videoUrl.value.trim()
    const platform = result.value.platform.toLowerCase()
    
    // 检查是否是YouTube - 使用专门的YouTube音频下载API
    if (platform === 'youtube' || originalPageUrl.includes('youtube.com') || originalPageUrl.includes('youtu.be')) {
      ElMessage.info(`正在下载 ${option.format} 格式音频，这可能需要1-3分钟，请耐心等待...`)
      
      try {
        // 调用YouTube音频下载API - 使用AbortController设置超时
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 300000) // 5分钟超时
        
        const response = await api.post('/api/audio/download', { 
            url: originalPageUrl,
            format: option.formatId || 'mp3_320'
          }, { signal: controller.signal })
        
        clearTimeout(timeoutId)
        
        const data = response.data
        
        stopProgress()
        
        if (data.success && data.download_url) {
          // 触发下载
          const fullDownloadUrl = 'http://localhost:8000' + data.download_url
          const link = document.createElement('a')
          link.href = fullDownloadUrl
          link.download = data.file_name || 'audio.' + option.format.toLowerCase()
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          ElMessage.success(`${option.format} 音频下载成功！文件大小: ${data.file_size_str}`)
        } else {
          ElMessage.error(data.error || '音频下载失败，请重试')
        }
      } catch (fetchError: any) {
        stopProgress()
        if (fetchError.name === 'AbortError') {
          ElMessage.error('下载超时，请重试或尝试其他格式')
        } else {
          ElMessage.error('下载失败: ' + fetchError.message)
        }
      }
    } else {
      // 其他平台使用原有的音频提取API（只支持MP3）
      const response = await api.post('/api/parse/extract-audio', { original_url: originalPageUrl })
      const data = response.data
      
      stopProgress()
      
      if (data.success && data.audio_url) {
        window.open(data.audio_url, '_blank')
        ElMessage.success('已在新窗口打开音频，可右键保存下载')
      } else {
        ElMessage.error(data.message || '音频提取失败')
      }
    }
  } catch (err: any) {
    console.error('Audio download error:', err)
    if (err.response?.status === 401 || err.response?.status === 402) return
    ElMessage.error('音频下载失败: ' + (err.response?.data?.detail || err.message))
  } finally {
    option.downloading = false
    extracting.value = false
    stopProgress()
  }
}

const handleDownload = async (option: QualityOption) => {
  if (!result.value) return
  
  option.downloading = true
  dialogMode.value = 'extract'
  extracting.value = true
  startProgress()
  
  try {
    // 使用option中保存的原始URL，而不是输入框的当前值
    const originalPageUrl = option.originalUrl || videoUrl.value.trim()
    
    console.log('Starting download for:', originalPageUrl)
    console.log('Platform from result:', result.value?.platform)
    
    // 判断是否是需要服务器端下载的平台 (YouTube, B站, 抖音, 小红书, 快手, TikTok等)
    const isStreamingPlatform = 
      originalPageUrl.includes('youtube.com') || 
      originalPageUrl.includes('youtu.be') ||
      originalPageUrl.includes('bilibili.com') ||
      originalPageUrl.includes('b23.tv') ||
      originalPageUrl.includes('douyin.com') ||
      originalPageUrl.includes('v.douyin.com') ||
      originalPageUrl.includes('iesdouyin.com') ||
      originalPageUrl.includes('xiaohongshu.com') ||
      originalPageUrl.includes('xhslink.com') ||
      originalPageUrl.includes('kuaishou.com') ||
      originalPageUrl.includes('v.kuaishou.com') ||
      originalPageUrl.includes('tiktok.com')
    
    if (isStreamingPlatform) {
      // 使用服务器端下载获取完整MP4
      dialogMode.value = 'download'
      extracting.value = true
      startProgress()
      ElMessage.info('正在服务器端下载视频并合并转码，这可能会耗时1-3分钟...')
      
      try {
        const downloadResponse = await api.post('/api/parse/server-download', { 
            original_url: originalPageUrl,
            quality: option.quality
          })
        const downloadResult = downloadResponse.data
        
        if (downloadResult.success && downloadResult.download_url) {
          stopProgress()
          
          const fullDownloadUrl = 'http://localhost:8000' + downloadResult.download_url
          extractedVideoUrl.value = fullDownloadUrl
          
          const link = document.createElement('a')
          link.href = fullDownloadUrl
          link.download = 'video.mp4'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          ElMessage.success(`视频下载成功！文件大小: ${Math.round(downloadResult.file_size / 1024 / 1024)}MB`)
        } else {
          throw new Error(downloadResult.message || '服务器下载失败')
        }
      } catch (serverErr: any) {
        console.warn('Server download failed, falling back to direct extraction:', serverErr)
        ElMessage.warning(`服务器代理下载失败 (${serverErr.message})，正在尝试提取无水印直链作为备用...`)
        
        // 容灾降级：尝试直接提取原始无水印链接
        const extractResponse = await api.post('/api/parse/extract-unified', { url: originalPageUrl })
        const extractResult = extractResponse.data
        if (extractResult.success && extractResult.video_url) {
          stopProgress()
          extractedVideoUrl.value = extractResult.video_url
          ElMessage.success('已为您提取到备用无水印直链，请点击下方链接在新标签页打开并右键保存。')
        } else {
          throw new Error(serverErr.message) // 抛出原始错误
        }
      }
      
    } else {
      // 其他平台使用统一提取
      const extractResponse = await api.post('/api/parse/extract-unified', { 
          url: originalPageUrl
        })
      const extractResult = extractResponse.data
      
      console.log('Unified extract result:', extractResult)
      
      if (extractResult.success && extractResult.video_url) {
        stopProgress()
        
        // 检查是否是M3U8链接，如果是则使用服务器下载
        if (extractResult.video_url.includes('m3u8') || extractResult.video_url.includes('manifest')) {
          dialogMode.value = 'download'
          startProgress()
          ElMessage.warning('检测到流媒体格式，转用云端高速分片转码下载...')
          
          try {
            // 递归调用服务器下载
            const serverResponse = await api.post('/api/parse/server-download', { original_url: originalPageUrl })
            const serverResult = serverResponse.data
            if (serverResult.success && serverResult.download_url) {
              const fullDownloadUrl = 'http://localhost:8000' + serverResult.download_url
              extractedVideoUrl.value = fullDownloadUrl
              
              const link = document.createElement('a')
              link.href = fullDownloadUrl
              link.download = 'video.mp4'
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
              
              ElMessage.success(`视频已下载！(${Math.round(serverResult.file_size / 1024 / 1024)}MB)`)
            } else {
              throw new Error(serverResult.message || '服务器端云下载失败')
            }
          } catch (serverErr: any) {
            console.warn('Server download failed for M3U8, keeping original URL:', serverErr)
            stopProgress()
            extractedVideoUrl.value = extractResult.video_url
            ElMessage.warning(`云端下载失败 (${serverErr.message})，已为您保留原始流媒体直链，请手动复制下载。`)
          }
        } else {
          // 保存提取到的视频链接供用户手动点击
          extractedVideoUrl.value = extractResult.video_url
          
          // 尝试打开新窗口
          const link = document.createElement('a')
          link.href = extractResult.video_url
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          
          ElMessage.success('视频链接已提取！如未自动打开，请点击下方链接')
        }
        
        // 复制链接到剪贴板
        try {
          await navigator.clipboard.writeText(extractedVideoUrl.value)
        } catch (e) {
          console.log('Clipboard not available')
        }
        
      } else {
        ElMessage.error(extractResult.message || '未能提取到视频链接')
      }
    }
    
  } catch (err: any) {
    console.error('Download error:', err)
    if (err.response?.status === 401 || err.response?.status === 402) return
    ElMessage.error('下载失败: ' + (err.response?.data?.detail || err.message))
  } finally {
    option.downloading = false
    extracting.value = false
    stopProgress()
  }
}

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  if (hours > 0) {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
  }
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}
</script>

<style scoped>
.parser-page {
  padding: 40px 20px;
  background: #f8fafc;
  min-height: 100vh;
}

.parser-container {
  max-width: 1000px;
  margin: 0 auto;
}

.header-section {
  text-align: center;
  margin-bottom: 40px;
  color: #1e293b;
}

.header-section h1 {
  margin-bottom: 10px;
  font-size: 32px;
  font-weight: 800;
  text-shadow: none;
}

.header-section p {
  color: #64748b;
  font-size: 16px;
}

.input-section {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.url-input {
  flex: 1;
}

.url-input :deep(.el-input__wrapper) {
  border-radius: 12px;
}

.parse-btn {
  padding: 0 30px;
  font-weight: 600;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.parse-btn:hover {
  background: linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%);
}

/* Autocomplete styles */
.autocomplete-item {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  position: relative;
  padding-right: 40px;
}

.url-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.platform-tag {
  flex-shrink: 0;
}

.url-title {
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-text {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-time {
  font-size: 11px;
  color: #c0c4cc;
  margin-top: 2px;
}

.delete-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.6;
}

.delete-btn:hover {
  opacity: 1;
}

/* Recent Links */
.recent-links {
  background: rgba(255,255,255,0.95);
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.recent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.recent-title {
  font-weight: 600;
  color: #303133;
}

.recent-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.recent-tag {
  cursor: pointer;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
}

.recent-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.tag-icon {
  margin-right: 4px;
}

.error-alert {
  margin-bottom: 20px;
  border-radius: 12px;
}

.result-section {
  margin-bottom: 40px;
}

.video-info-card {
  margin-bottom: 20px;
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.video-header {
  display: flex;
  gap: 20px;
  align-items: center;
}

.video-thumbnail {
  width: 200px;
  height: 112px;
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 32px;
}

.video-info h2 {
  margin: 0 0 10px;
  font-size: 18px;
  color: #303133;
}

.video-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.author {
  color: #606266;
}

.duration {
  color: #409eff;
  font-weight: 500;
}

.quality-table-card {
  border-radius: 16px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.quality-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.quality-text {
  font-weight: 600;
  font-size: 16px;
}

.file-size {
  font-weight: 500;
  color: #606266;
}

.quality-note {
  margin-top: 20px;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 500;
}

.supported-platforms {
  background: rgba(255,255,255,0.95);
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.supported-platforms h3 {
  font-size: 16px;
  color: #303133;
  margin: 0 0 16px;
}

.platform-tags {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.platform-tags .el-tag {
  font-size: 13px;
}

.extracting-content {
  text-align: center;
  padding: 20px;
}

.extracting-content .el-icon {
  color: #409eff;
  margin-bottom: 15px;
}

.extracting-content p {
  margin: 10px 0;
  color: #606266;
}

.extracting-content .tip {
  font-size: 12px;
  color: #909399;
}

.extracting-content .el-progress {
  margin-top: 20px;
}

/* Download Tabs Styles */
.download-tabs {
  border: none;
}

.download-tabs :deep(.el-tabs__header) {
  margin-bottom: 0;
  border-bottom: 2px solid #e4e7ed;
}

.download-tabs :deep(.el-tabs__item) {
  font-weight: 500;
  padding: 0 20px;
}

.download-tabs :deep(.el-tabs__item.is-active) {
  color: #409eff;
}

.tab-label {
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 嗅探资源展示区 */
.ingested-section {
  margin-top: 20px;
}

.ingested-card {
  border-radius: 12px;
  border: 1px solid #67c23a;
  background-color: #f0f9eb;
}

.ingested-card :deep(.el-card__header) {
  padding: 12px 20px;
  border-bottom: 1px solid #e1f3d8;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.pulse-icon {
  color: #67c23a;
  font-size: 20px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

.header-title {
  font-weight: 600;
  color: #333;
}

.ingested-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ingested-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: all 0.3s;
}

.ingested-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.ingested-thumb {
  width: 100px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
}

.ingested-info {
  flex: 1;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ingested-title {
  font-weight: 500;
  font-size: 14px;
  color: #333;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ingested-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ingested-time {
  font-size: 12px;
  color: #999;
}

.ingested-actions {
  padding-left: 10px;
}

.ingested-empty {
  padding: 10px 0;
  opacity: 0.8;
}

/* 提取到的视频链接区域 */
.extracted-url-section {
  margin-top: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-radius: 12px;
  border: 2px solid #4caf50;
}

.extracted-url-section .url-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #2e7d32;
  margin-bottom: 12px;
}

.extracted-url-section .video-link {
  display: block;
  padding: 12px;
  background: white;
  border-radius: 8px;
  color: #1565c0;
  text-decoration: none;
  word-break: break-all;
  font-family: monospace;
  font-size: 13px;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
}

.extracted-url-section .video-link:hover {
  background: #f5f5f5;
  text-decoration: underline;
}

.extracted-url-section .url-actions {
  display: flex;
  gap: 10px;
}

@media (max-width: 768px) {
  .input-section {
    flex-direction: column;
  }
  
  .video-header {
    flex-direction: column;
  }
  
  .video-thumbnail {
    width: 100%;
    height: 200px;
  }
  
  .recent-tag {
    max-width: 150px;
  }
  
  .tab-label {
    font-size: 12px;
  }
  
  .extracted-url-section .url-actions {
    flex-direction: column;
  }
}
</style>
